import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Shortcut } from "@/components/ui/shortcut";
import { useCreateSupplier } from "@/queries/suppliers/create";
import { zodResolver } from "@hookform/resolvers/zod";
import i18next from "i18next";
import * as React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as z from "zod";

const formSchema = z.object({
  supplierName: z.string().min(1, i18next.t("name_required")),
  isCategory: z.boolean().default(false),
});

type FormValues = z.infer<typeof formSchema>;

export function CreateCounterpartyModal({
  open,
  onOpenChange,
  onSuccess,
  type,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  type: "creditor" | "debtor";
}) {
  const { t } = useTranslation();
  const submitButtonRef = React.useRef<HTMLButtonElement>(null);
  const [createSupplier, { loading }] = useCreateSupplier({ type });

  // Platform detection and shortcut setup
  const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
  const shortcutStyle = "bg-white/20 border border-white/30 text-white";

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      supplierName: "",
      isCategory: false,
    },
  });

  // Keyboard shortcut: Cmd+Enter / Ctrl+Enter submits form
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === "Enter" &&
        (e.metaKey || e.ctrlKey) &&
        submitButtonRef.current
      ) {
        e.preventDefault();
        e.stopImmediatePropagation(); // Stop ALL other listeners on this event
        submitButtonRef.current.click();
      }
    };

    // Use capture phase to ensure we fire BEFORE other listeners
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, []);

  // Reset form when modal opens
  React.useEffect(() => {
    if (open) {
      form.reset({
        supplierName: "",
        isCategory: false,
      });
    }
  }, [open, form]);

  const onSubmit = async (data: FormValues) => {
    try {
      // Backend will auto-generate datevAccountNumber
      await createSupplier({
        supplierName: data.supplierName,
        status: "CONFIRMED",
        isCategory: data.isCategory,
      });

      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error("Failed to create supplier:", error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {
              {
                creditor: t("creditors.create.title"),
                debtor: t("debtors.create.title"),
              }[type]
            }
          </DialogTitle>
          <DialogDescription className="hidden" />
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <FormLabel>{t("account_number")}</FormLabel>
              <p className="text-sm text-muted-foreground mt-1">
                {t("suppliers.create.account_number_hint")}
              </p>
            </div>
            <FormField
              control={form.control}
              name="supplierName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("name")}</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Amazon" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="isCategory"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {
                      {
                        creditor: t("creditors.group"),
                        debtor: t("debtors.group"),
                      }[type]
                    }
                  </FormLabel>
                  <label
                    htmlFor="isCategory"
                    className={`flex items-start space-x-3 rounded-md border p-4 cursor-pointer hover:bg-accent/50 transition-colors ${
                      field.value
                        ? "border-blue-600 bg-blue-50 dark:border-blue-900 dark:bg-blue-950"
                        : "border-input"
                    }`}
                  >
                    <FormControl>
                      <Checkbox
                        id="isCategory"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        className="data-[state=checked]:border-blue-600 data-[state=checked]:bg-blue-600 data-[state=checked]:text-white dark:data-[state=checked]:border-blue-700 dark:data-[state=checked]:bg-blue-700"
                      />
                    </FormControl>
                    <div className="flex-1 space-y-1">
                      <div className="text-sm font-medium leading-none">
                        {
                          {
                            creditor: t("creditors.group_checkbox_label"),
                            debtor: t("debtors.group_checkbox_label"),
                          }[type]
                        }
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {
                          {
                            creditor: t("creditors.group_hint"),
                            debtor: t("debtors.group_hint"),
                          }[type]
                        }
                      </p>
                    </div>
                  </label>
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                {t("cancel")}
              </Button>
              <Button
                ref={submitButtonRef}
                type="submit"
                disabled={loading}
                className="gap-3"
              >
                {t("suppliers.create.submit")}
                <Shortcut.ShortcutRoot>
                  {isMac ? (
                    <Shortcut.ShortcutCommand
                      size="sm"
                      className={shortcutStyle}
                    />
                  ) : (
                    <Shortcut.ShortcutHotKey
                      size="sm"
                      className={`${shortcutStyle} w-[36px]`}
                    >
                      Ctrl
                    </Shortcut.ShortcutHotKey>
                  )}
                  <Shortcut.ShortcutEnter size="sm" className={shortcutStyle} />
                </Shortcut.ShortcutRoot>
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
